import { useEffect, useState } from 'react';
import { IConfig } from '../../../../Interfaces/IConfig';
import { ILegalClient } from '../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptyGeneralRelationshipData,
  IGeneralRelationshipData,
} from '../../../../Interfaces/ILegalTransactionRelationship/Relationships/General/ILegalTransactionRelationshipGeneralData';
import { DialogFrame } from '../../../core/DialogFrame';
import Send from '../../../generic/Send';
import {
  createChildren,
  createStepperArray,
  ISavedRelationshipDialogData,
  TransitionStatus,
  updateOrCreateRelationship,
} from '../generic/utilities';
import { RestoreProgress } from '../RestoreProgress';
import { SaveProgress } from '../SaveProgress';
import { GeneralEditLegalClients } from './slides/GeneralEditLegalClients';
import { GeneralStart } from './slides/GeneralStart';
import { GeneralSummary } from './slides/GeneralSummary';
import { getOrCreateGeneralRelationship } from './utilities';

interface IGeneralDialogProps {
  configObject: IConfig;
}

type ISavedGeneralData = ISavedRelationshipDialogData<IGeneralRelationshipData>;

const GeneralDialog: React.FC<IGeneralDialogProps> = ({ configObject }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [previousPage, setPreviousPage] = useState(-1);
  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(
    TransitionStatus.IDLE
  );

  const [relationshipArray, setRelationshipArray] = useState(
    [] as ILegalTransactionRelationship[]
  );
  const [generalData, setGeneralData] = useState(
    getEmptyGeneralRelationshipData()
  );

  useEffect(() => {
    const relationship = getOrCreateGeneralRelationship(relationshipArray);

    if (relationship.data !== generalData) {
      const updatedRelationship = {
        ...relationship,
        data: generalData,
      };
      setRelationshipArray(
        updateOrCreateRelationship(relationshipArray, updatedRelationship)
      );
    }
  }, [generalData, relationshipArray]);

  const handleBack = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage - 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };
  const handleNext = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage + 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };

  const onEntered = () => {
    setTransitionStatus(TransitionStatus.IDLE);
  };

  const onExited = () => {
    if (transitionStatus === TransitionStatus.EXITING) {
      setTransitionStatus(TransitionStatus.ENTERING);
    } else {
      setTransitionStatus(TransitionStatus.IDLE);
    }
  };

  const [legalClientArray, setLegalClientArray] = useState(
    [] as ILegalClient[]
  );

  const steps = [
    {
      name: 'Start',
      content: <GeneralStart handleBack={handleBack} handleNext={handleNext} />,
    },
    {
      name: 'Personen',
      content: (
        <GeneralEditLegalClients
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Zusammenfassung',
      content: (
        <GeneralSummary
          configObject={configObject}
          generalRelationshipData={generalData}
          setGeneralRelationshipData={setGeneralData}
          legalClientArray={legalClientArray}
          setLegalClientArray={setLegalClientArray}
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Abschluss',
      content: <Send />,
    },
  ];

  const restoreSetterFunction = (savedData: ISavedGeneralData) => {
    const { relationshipArray, legalClientArray, data } = savedData;
    setRelationshipArray(relationshipArray);
    setLegalClientArray(legalClientArray);
    setGeneralData(data);
    setCurrentPage(0);
    setPreviousPage(-1);
    setTransitionStatus(TransitionStatus.IDLE);
  };
  const dataToSave = (): ISavedGeneralData => {
    return {
      relationshipArray,
      legalClientArray,
      data: generalData,
    };
  };

  return (
    <DialogFrame
      configObject={configObject}
      title={'Formular für allgemeine Anliegen'}
      currentPage={currentPage}
      stepperArray={createStepperArray({ steps, currentPage })}
      headerButtonArray={[
        <RestoreProgress
          key='general-import'
          setterFunction={restoreSetterFunction}
        ></RestoreProgress>,
        <SaveProgress
          key='general-export'
          idLegaltransaction={-1}
          getDataToSave={dataToSave}
          filename={'Allgemeines Anliegen'}
        ></SaveProgress>,
      ]}
      txt={''}
    >
      {createChildren({
        steps,
        currentPage,
        previousPage,
        transitionStatus,
        onEntered,
        onExited,
      })}
    </DialogFrame>
  );
};

export default GeneralDialog;
